import { type FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Button, IconButton, SpinnerLoader, Typography } from 'shared/ui'
import { type MoreDropdownProps } from 'shared/ui/Actions/Actions'
import { makeTextFieldAutoFocus, makeTextFieldElement, TextField } from 'shared/ui/TextField'
import { replaceLink } from 'shared/lib'
import { type IRequestReview } from 'entities/Review'
import { useMessageFieldContext } from 'widgets/MessageField/context/MessageFieldContext'
import { RequestReviewStore } from 'widgets/RequestReview/store/requestReviewStore'
import styles from './styles.module.scss'

export const RequestReviewContent: FC<MoreDropdownProps> = observer(({ onCloseMenu }) => {
  const messageFieldStore = useMessageFieldContext()
  const [store] = useState(() => new RequestReviewStore())
  const { loading, search, setSearch, reviews, error } = store

  useEffect(() => () => store.dispose(), [store])

  const onBack = () => {
    messageFieldStore.setMoreDropdownComponent(null)
  }

  const onSelectReview = (review: IRequestReview) => {
    const text = `We'd love your feedback. Rate your experience here: ${replaceLink(
      review.short_url
    )}`
    messageFieldStore.addContent && messageFieldStore.addContent(text + '&nbsp;')
    onCloseMenu?.()
  }

  const onCreateReviewPage = () => {
    window.open('/reviews/create', '_blank')
    onCloseMenu?.()
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loading}>
          <SpinnerLoader ariaLabel={'RequestReview'} size={16} />
        </div>
      )
    }

    if (!error && !reviews.length && !search.trim().length) {
      return (
        <Button
          typeBtn={'informative'}
          text={'Create review page'}
          fullWidth
          onClick={onCreateReviewPage}
        />
      )
    }

    return (
      <div className={styles.list}>
        {!reviews.length && (
          <div className={styles.wrapEmpty}>
            <Typography
              variant={'body-sm-regular'}
              color={'var(--content-primary-tertiary)'}
              ariaLabel={'No results found'}
            >
              No results found
            </Typography>
          </div>
        )}
        {reviews.map((review) => (
          <Button
            key={review.id}
            typeBtn={'menuItem'}
            text={review.name}
            fullWidth
            onClick={() => onSelectReview(review)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className={classNames(styles.wrap, 'top-dropdown-column-reverse-content')}>
      <TextField
        className={styles.textInput}
        size={'small'}
        variant={'integrated'}
        ariaLabel={'RequestReviewSearch'}
        InputProps={{ placeholder: 'Search' }}
        value={search}
        onChange={setSearch}
        mainActions={[makeTextFieldAutoFocus({ withFocus: !loading })]}
        leftActions={[
          makeTextFieldElement({
            element: (
              <IconButton
                tag={'div'}
                icon={'chevronLeft'}
                size={'small'}
                variant={'icon'}
                color={'secondary'}
                fontSize={15}
                onClick={onBack}
                ariaLabel={'RequestReviewContent_back'}
              />
            ),
          }),
        ]}
      />
      <div className={styles.divider} />
      <div className={styles.content}>{renderContent()}</div>
    </div>
  )
})
